import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import EarthCanvas from "../canvas/Earth";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-top: 50px;
  position: relative;
  z-index: 1;
  align-items: center;
  background-color: transparent;
  padding: 40px 16px;
`;

const Wrapper = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-direction: column;
  width: 100%;
  max-width: 1100px;
  gap: 12px;
`;

const SectionTitle = styled.h2`
  font-size: 2rem;
  color: ${({ theme }) => theme.white};
  margin-bottom: 2rem;
  font-family: 'JetBrains Mono', monospace;
  letter-spacing: -0.02em;
  align-self: flex-start;

  &::before {
    content: "~/ ";
    color: ${({ theme }) => theme.primary};
  }

  @media (max-width: 768px) {
    font-size: 1.6rem;
  }
`;

const Desc = styled.div`
  font-size: 18px;
  text-align: center;
  font-weight: 500;
  color: ${({ theme }) => theme.text_secondary};
  margin-bottom: 40px;
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

// Terminal Mockup Styles
const TerminalContainer = styled.div`
  width: 100%;
  max-width: 800px;
  background: #1e1e1e;
  border: 1px solid #333;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.5);
  font-family: 'JetBrains Mono', monospace;
  margin-bottom: 40px;
  cursor: text;
`;

const WindowControls = styled.div`
  display: flex;
  gap: 8px;
  padding: 12px 16px;
  background: #252526;
  border-bottom: 1px solid #333;
`;

const Dot = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: ${props => props.$color};
`;

const TerminalHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 1rem;
  background: #1e1e1e;
  border-bottom: 1px solid #333;
  font-size: 0.85rem;
  color: #888;
`;

const ConsolePanel = styled.div`
  background: #121212;
  padding: 1.5rem;
  height: 380px;
  overflow-y: auto;
  font-size: 0.85rem;
  text-align: left;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  color: #e0e0e0;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: #121212;
  }
  &::-webkit-scrollbar-thumb {
    background: #333;
    border-radius: 3px;
  }
`;

const CommandLine = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
`;

const PromptText = styled.span`
  color: var(--spring-green, #8bc34a);
  font-weight: bold;
  flex-shrink: 0;
`;

const InputField = styled.input`
  background: transparent;
  border: none;
  outline: none;
  color: #e0e0e0;
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.85rem;
  width: 100%;
  caret-color: var(--spring-green, #8bc34a);
`;

const OutputBlock = styled.div`
  white-space: pre-wrap;
  line-height: 1.5;
`;

// Syntax highlighting styles for JSON outputs
const KeySpan = styled.span`
  color: #9cdcfe;
`;

const StringSpan = styled.span`
  color: #ce9178;
`;

const NumberSpan = styled.span`
  color: #b5cea8;
`;

const BooleanSpan = styled.span`
  color: #569cd6;
`;

const BraceSpan = styled.span`
  color: #d4d4d4;
`;

const EDUCATION_DETAILS = {
  "college.json": {
    institution: "Sagar institute of Science Technology and Research, Bhopal",
    degree: "Bachelor of Technology - BTech, Computer Science and Engineering",
    date: "Aug 2022 - Sep 2026",
    grade: "7.49 CGPA",
    status: "Completed 7 semesters",
    description: "Currently pursuing BTech in CSE. Working on exciting full stack web applications and machine learning projects."
  },
  "12th.json": {
    institution: "Saraswati Higher secondary school Burhar, Shahdol, M.P.",
    degree: "MPBSE(XII), Science with PCM",
    date: "Apr 2021 - Apr 2022",
    grade: "88%",
    description: "Completed higher secondary education majoring in Physics, Chemistry, and Mathematics (PCM)."
  },
  "10th.json": {
    institution: "Saraswati Higher secondary school Burhar, Shahdol, M.P.",
    degree: "MPBSE(X), Science with PCM",
    date: "Apr 2019 - Apr 2020",
    grade: "89%",
    description: "Completed secondary high school education with high academic performance."
  }
};

const Education = () => {
  const [history, setHistory] = useState([
    {
      type: "output",
      text: "Welcome to Lucky Paswan's Education Terminal.\nType 'help' to see list of commands or 'ls' to see educational files."
    },
    {
      type: "input",
      text: "ls"
    },
    {
      type: "output",
      text: "college.json    12th.json    10th.json"
    },
    {
      type: "input",
      text: "cat college"
    },
    {
      type: "json",
      data: EDUCATION_DETAILS["college.json"]
    },
    {
      type: "input",
      text: "cat 12th"
    },
    {
      type: "json",
      data: EDUCATION_DETAILS["12th.json"]
    },
    {
      type: "input",
      text: "cat 10th"
    },
    {
      type: "json",
      data: EDUCATION_DETAILS["10th.json"]
    }
  ]);
  const [inputVal, setInputVal] = useState("");
  const inputRef = useRef(null);
  const consoleEndRef = useRef(null);

  useEffect(() => {
    if (consoleEndRef.current) {
      consoleEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [history]);

  const focusInput = () => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleCommand = (e) => {
    if (e.key === "Enter") {
      const commandText = inputVal.trim();
      setInputVal("");

      if (!commandText) return;

      const newHistory = [...history, { type: "input", text: commandText }];

      const parts = commandText.toLowerCase().split(/\s+/);
      const mainCmd = parts[0];
      const arg = parts[1];

      if (mainCmd === "clear") {
        setHistory([]);
        return;
      }

      if (mainCmd === "help") {
        newHistory.push({
          type: "output",
          text: "Available commands:\n  ls                       - List educational JSON files\n  cat <filename>           - Display education file details (e.g. cat college.json)\n  clear                    - Clear terminal console\n  help                     - Show command manual"
        });
      } else if (mainCmd === "ls") {
        newHistory.push({
          type: "output",
          text: "college.json    12th.json    10th.json"
        });
      } else if (mainCmd === "cat") {
        if (!arg) {
          newHistory.push({
            type: "output",
            text: "usage: cat <filename> (e.g. cat college.json)"
          });
        } else {
          // Normalize file names to make command user friendly
          let filename = arg;
          if (filename === "college" || filename === "college.json") {
            filename = "college.json";
          } else if (filename === "12th" || filename === "12" || filename === "12th.json") {
            filename = "12th.json";
          } else if (filename === "10th" || filename === "10" || filename === "10th.json") {
            filename = "10th.json";
          }

          if (EDUCATION_DETAILS[filename]) {
            newHistory.push({
              type: "json",
              data: EDUCATION_DETAILS[filename]
            });
          } else {
            newHistory.push({
              type: "output",
              text: `cat: ${arg}: No such file or directory. Type 'ls' to see files.`
            });
          }
        }
      } else {
        newHistory.push({
          type: "output",
          text: `bash: command not found: ${commandText}. Type 'help' for instructions.`
        });
      }

      setHistory(newHistory);
    }
  };

  const renderJson = (obj) => {
    const jsonString = JSON.stringify(obj, null, 2);
    const lines = jsonString.split("\n");
    return lines.map((line, idx) => {
      const keyRegex = /^(\s*)"([^"]+)"(\s*:\s*)/;
      const keyMatch = line.match(keyRegex);
      
      if (keyMatch) {
        const spacing = keyMatch[1];
        const key = keyMatch[2];
        const colon = keyMatch[3];
        const rest = line.substring(keyMatch[0].length);
        
        let valSpan = <span>{rest}</span>;
        if (rest.trim().startsWith('"')) {
          valSpan = <StringSpan>{rest}</StringSpan>;
        } else if (/^\d+/.test(rest.trim())) {
          valSpan = <NumberSpan>{rest}</NumberSpan>;
        } else if (/^(true|false)/.test(rest.trim())) {
          valSpan = <BooleanSpan>{rest}</BooleanSpan>;
        }

        return (
          <div key={idx}>
            {spacing}
            <KeySpan>"{key}"</KeySpan>
            {colon}
            {valSpan}
          </div>
        );
      }

      if (line.trim() === "{" || line.trim() === "}" || line.trim() === "[" || line.trim() === "]") {
        return <div key={idx}><BraceSpan>{line}</BraceSpan></div>;
      }

      if (line.trim().startsWith('"')) {
        return <div key={idx}><StringSpan>{line}</StringSpan></div>;
      }

      return <div key={idx}>{line}</div>;
    });
  };

  return (
    <Container id="Education">
      <Wrapper>
        <SectionTitle>Education.sh</SectionTitle>
        <Desc>
          Type commands in my Linux bash terminal mockup below to check my education details.
        </Desc>

        <TerminalContainer onClick={focusInput}>
          <WindowControls>
            <Dot $color="#ff5f56" />
            <Dot $color="#ffbd2e" />
            <Dot $color="#27c93f" />
          </WindowControls>
          
          <TerminalHeader>
            <svg fill="none" height="14" stroke="#888" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" viewBox="0 0 24 24" width="14">
              <path d="M4 17l6-6-6-6M12 19h8"></path>
            </svg>
            bash - terminal (Education)
          </TerminalHeader>

          <ConsolePanel>
            {history.map((line, idx) => {
              if (line.type === "input") {
                return (
                  <CommandLine key={idx}>
                    <PromptText>lucky7649@portfolio:~$</PromptText>
                    <span>{line.text}</span>
                  </CommandLine>
                );
              } else if (line.type === "json") {
                return (
                  <OutputBlock key={idx}>
                    {renderJson(line.data)}
                  </OutputBlock>
                );
              } else {
                return (
                  <OutputBlock key={idx}>
                    {line.text}
                  </OutputBlock>
                );
              }
            })}
            
            <CommandLine>
              <PromptText>lucky7649@portfolio:~$</PromptText>
              <InputField 
                ref={inputRef}
                type="text" 
                value={inputVal}
                onChange={(e) => setInputVal(e.target.value)}
                onKeyDown={handleCommand}
                placeholder=""
                autoFocus
              />
            </CommandLine>
            <div ref={consoleEndRef} />
          </ConsolePanel>
        </TerminalContainer>

        <EarthCanvas />
      </Wrapper>
    </Container>
  );
};

export default Education;
